import React, { useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown, Check } from 'lucide-react'
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/src/renderer/components/ui/dropdown-menu'
import { REWARD_CATEGORIES, categoryName, type CatalogLanguage } from '@/src/renderer/lib/data/rewardCatalog'
import { RewardCategoryIcon } from '../../icons'
import type { RewardCategoryFilterProps } from './RewardPicker.types'

/**
 * Category filter of the reward picker (Figma 3332:48995).
 *
 * Multi-select like the tag dropdown: an empty selection means "all
 * categories", and the trigger shows how many are active. Picking "Alle
 * Kategorien" clears the restriction.
 */
export const RewardCategoryFilter: React.FC<RewardCategoryFilterProps> = ({ value, onValueChange }) => {
  const { t, i18n } = useTranslation()
  const language: CatalogLanguage = i18n.language?.startsWith('en') ? 'en' : 'de'

  const toggle = useCallback((categoryId: string) => {
    onValueChange(
      value.includes(categoryId)
        ? value.filter(id => id !== categoryId)
        : [...value, categoryId],
    )
  }, [value, onValueChange])

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button type="button" className="reward-category-filter__trigger">
          <RewardCategoryIcon slug="checkAll" />
          <span className="reward-category-filter__label">{t('taskCreation.rewardPicker.allCategories')}</span>
          {value.length > 0 && (
            <span className="reward-category-filter__count">{value.length}</span>
          )}
          <ChevronDown size={16} className="reward-category-filter__chevron" />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="reward-category-filter__menu">
        <DropdownMenuItem
          className="context-menu-content__item"
          onSelect={() => onValueChange([])}
        >
          <RewardCategoryIcon slug="checkAll" className="context-menu-content__item-icon" />
          {t('taskCreation.rewardPicker.allCategories')}
          {value.length === 0 && <Check size={16} className="context-menu-content__item-check" />}
        </DropdownMenuItem>

        <DropdownMenuSeparator />
        <div className="reward-category-filter__section-label">
          {t('taskCreation.rewardPicker.categoriesLabel')}
        </div>

        {REWARD_CATEGORIES.map(category => (
          <DropdownMenuItem
            key={category.id}
            className="context-menu-content__item"
            // Keep the menu open: picking several categories in a row is the
            // whole point of a multi-select.
            onSelect={(event) => {
              event.preventDefault()
              toggle(category.id)
            }}
          >
            <RewardCategoryIcon slug={category.slug} className="context-menu-content__item-icon" />
            {categoryName(category, language)}
            {value.includes(category.id) && (
              <Check size={16} className="context-menu-content__item-check" />
            )}
          </DropdownMenuItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  )
}
